import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { PageHeader, StatCard, Section, EmptyState } from "../../components/Common";
import {
  Users, GraduationCap, Wallet, BookOpen, ClipboardList, Bell,
  ShieldCheck, ArrowRight, Baby, User as UserIcon, Layers,
} from "lucide-react";
import { Link } from "react-router-dom";
import { formatCurrency } from "../../lib/currency";
import { useDataSync } from "../../lib/realtime";

const QUICK_LINKS = [
  { to: "/dashboard/admin/users", label: "Users", icon: Users, color: "bg-blue-50 text-blue-600" },
  { to: "/dashboard/admin/batches", label: "Batches", icon: Layers, color: "bg-indigo-50 text-indigo-600" },
  { to: "/dashboard/admin/fees", label: "Fees", icon: Wallet, color: "bg-emerald-50 text-emerald-600" },
  { to: "/dashboard/admin/subscriptions", label: "Subscriptions", icon: ShieldCheck, color: "bg-purple-50 text-purple-600" },
  { to: "/dashboard/admin/notifications", label: "Notifications", icon: Bell, color: "bg-amber-50 text-amber-600" },
  { to: "/dashboard/admin/settings", label: "Branding", icon: BookOpen, color: "bg-rose-50 text-rose-600" },
];

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [fees, setFees] = useState([]);
  const [homework, setHomework] = useState([]);
  const [tests, setTests] = useState([]);
  const [subReqs, setSubReqs] = useState([]);

  const load = async () => {
    try {
      const [s, u, b, f, h, t, sr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/batches"),
        api.get("/admin/fees"),
        api.get("/homework"),
        api.get("/tests"),
        api.get("/admin/subscription-requests"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setBatches(b.data);
      setFees(f.data);
      setHomework(h.data);
      setTests(t.data);
      setSubReqs(sr.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, []);
  useDataSync(["users", "batches", "fees", "homework", "tests", "subscriptions"], () => load());

  const pendingSubs = subReqs.filter((r) => r.status === "pending").length;
  const roleCounts = useMemo(() => {
    const map = { student: 0, teacher: 0, parent: 0, admin: 0 };
    users.forEach((u) => { map[u.role] = (map[u.role] || 0) + 1; });
    return map;
  }, [users]);

  const recentUsers = users.slice(-6).reverse();
  const recentHomework = homework.slice(0, 5);
  const recentTests = tests.slice(0, 5);
  const recentBatches = batches.slice(-4).reverse();
  const recentFees = fees.slice(0, 5);

  const teacherName = (id) => users.find((u) => u.id === id)?.name || "—";
  const studentName = (id) => users.find((u) => u.id === id)?.name || "—";
  const batchName = (id) => batches.find((b) => b.id === id)?.name || "—";

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <PageHeader
        title="Admin Command Center"
        subtitle="Everything you need to run your institute — at a glance."
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Students" value={stats?.students ?? "—"} icon={Users} />
        <StatCard label="Teachers" value={stats?.teachers ?? "—"} icon={GraduationCap} accent="mint" />
        <StatCard label="Batches" value={stats?.batches ?? "—"} icon={BookOpen} accent="yellow" />
        <StatCard label="Revenue" value={formatCurrency(stats?.fees?.revenue ?? 0)} icon={Wallet} accent="coral" />
      </div>

      {/* Quick links */}
      <Section title="Manage">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {QUICK_LINKS.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase()}`}
              className="group flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all">
              <div className={`w-11 h-11 rounded-xl grid place-items-center ${q.color}`}>
                <q.icon className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold text-slate-700 group-hover:text-blue-700">{q.label}</span>
            </Link>
          ))}
        </div>
      </Section>

      {/* Pending subscription requests alert */}
      {pendingSubs > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-center justify-between"
          data-testid="pending-subs-banner">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            <div>
              <div className="font-semibold text-amber-900">{pendingSubs} pending subscription request{pendingSubs === 1 ? "" : "s"}</div>
              <div className="text-xs text-amber-700">Review and approve to activate access.</div>
            </div>
          </div>
          <Link to="/dashboard/admin/subscriptions"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700">
            Review <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Fees */}
        <Section title="Fee status" actions={
          <Link to="/dashboard/admin/fees" className="text-sm font-semibold text-blue-600 hover:underline">Manage →</Link>
        }>
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Paid" value={stats?.fees?.paid ?? 0} tone="emerald" testId="fees-paid" />
            <StatBox label="Pending" value={stats?.fees?.pending ?? 0} tone="amber" testId="fees-pending" />
            <StatBox label="Overdue" value={stats?.fees?.overdue ?? 0} tone="rose" testId="fees-overdue" />
          </div>
          <div className="mt-4 space-y-1">
            {recentFees.length === 0 && <p className="text-sm text-slate-500">No fees recorded yet.</p>}
            {recentFees.map((f) => (
              <div key={f.id} className="flex items-center justify-between text-sm py-1.5">
                <span className="text-slate-700 truncate">{studentName(f.student_id)}</span>
                <span className="font-mono text-slate-900">{formatCurrency(f.amount, f.currency)}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Users breakdown */}
        <Section title="Users" actions={
          <Link to="/dashboard/admin/users" className="text-sm font-semibold text-blue-600 hover:underline">Manage →</Link>
        }>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <RoleTile icon={UserIcon} label="Students" count={roleCounts.student} tone="blue" />
            <RoleTile icon={GraduationCap} label="Teachers" count={roleCounts.teacher} tone="emerald" />
            <RoleTile icon={Baby} label="Parents" count={roleCounts.parent} tone="amber" />
            <RoleTile icon={ShieldCheck} label="Admins" count={roleCounts.admin} tone="purple" />
          </div>
          <div className="mt-2 space-y-1">
            {recentUsers.length === 0 && <p className="text-sm text-slate-500">No users yet.</p>}
            {recentUsers.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 shrink-0">
                  {u.role}
                </span>
              </div>
            ))}
          </div>
        </Section>

        {/* Homework */}
        <Section title="Recent homework" actions={
          <span className="text-xs text-slate-500">Total: {homework.length}</span>
        }>
          {recentHomework.length === 0 ? (
            <EmptyState title="No homework yet" desc="Teachers will post assignments here." icon={BookOpen} />
          ) : (
            <div className="space-y-2">
              {recentHomework.map((h) => (
                <div key={h.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50"
                  data-testid={`admin-hw-${h.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 grid place-items-center shrink-0">
                    <BookOpen className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{h.title}</div>
                    <div className="text-xs text-slate-500">
                      {batchName(h.batch_id)}{h.due_date ? ` · Due ${h.due_date}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Tests */}
        <Section title="Recent tests" actions={
          <span className="text-xs text-slate-500">Total: {tests.length}</span>
        }>
          {recentTests.length === 0 ? (
            <EmptyState title="No tests yet" desc="MCQ tests created will show here." icon={ClipboardList} />
          ) : (
            <div className="space-y-2">
              {recentTests.map((t) => (
                <div key={t.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50"
                  data-testid={`admin-test-${t.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center shrink-0">
                    <ClipboardList className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500">
                      {batchName(t.batch_id)} · {t.questions?.length || 0} Qs · {t.duration_minutes} min
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Batches */}
      <Section title="Batches" actions={
        <Link to="/dashboard/admin/batches" className="text-sm font-semibold text-blue-600 hover:underline">Manage →</Link>
      }>
        {recentBatches.length === 0 ? (
          <EmptyState title="No batches yet" desc="Create a batch to group students and teachers." icon={Layers} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {recentBatches.map((b) => (
              <Link key={b.id} to="/dashboard/admin/batches"
                data-testid={`admin-batch-${b.id}`}
                className="p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
                  <Layers className="h-4 w-4" />
                </div>
                <div className="mt-3 font-heading font-bold text-slate-900 truncate">{b.name}</div>
                <div className="text-xs text-slate-500 truncate">{b.subject}</div>
                {b.teacher_id && <div className="mt-2 text-xs text-slate-500">Teacher: <span className="font-semibold text-slate-700">{teacherName(b.teacher_id)}</span></div>}
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function StatBox({ label, value, tone, testId }) {
  const styles = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div className={`text-center p-3 rounded-lg ${styles[tone]}`} data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-wider">{label}</div>
      <div className="font-heading font-black text-2xl">{value}</div>
    </div>
  );
}

function RoleTile({ icon: Icon, label, count, tone }) {
  const bg = { blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", purple: "bg-purple-50 text-purple-700" }[tone];
  return (
    <div className={`p-3 rounded-lg ${bg} text-center`}>
      <Icon className="h-4 w-4 mx-auto mb-1" />
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading font-black text-lg">{count}</div>
    </div>
  );
}
